/**
 * Who was who.
 *
 * Yahoo only started attaching manager names to teams in 2009. Everything before that is a
 * team name and nothing else — "gold teef NDNs", "The Insane Israeli", "HowBoutThemHOKIES!!" —
 * and two of the ten teams in 2009/2010 ('roid rage) are unresolved too. That is six and a bit
 * seasons of matchups, standings and draft picks that the records engine has to refuse to count,
 * because a career table that quietly starts in 2011 while claiming 23 seasons is the most
 * plausible-looking wrong number the app could produce.
 *
 * The fix is not a scrape. Nobody can recover this from Yahoo; it lives in the memories of ten
 * men. So this module is the machinery for a HUMAN to pair them up — a commissioner sits down
 * once, works through 62 team names, and 2003-2010 comes online.
 *
 * Three things live here:
 *
 *   The overlay. A pairing is `season + team name -> GM`, keyed on the name rather than Yahoo's
 *   team id, because the team id is the one field the three archives do NOT share: matchups and
 *   standings carry the name, draft picks carry the name uppercased, and only the team-name
 *   history carries the id.
 *
 *   The suggestion engine. Most of these are not actually mysteries — a GM keeps a joke running
 *   for years, so "Samanticiders" appears unresolved in 2006-2008 and resolved to SAMANT in 2009.
 *   Exact-name carryover solves about half the board on its own; the rest gets ranked by shared
 *   distinctive words, weighted towards nearby seasons.
 *
 *   The read path. `gmFor` is what the records engine calls instead of trusting the raw field,
 *   so every board widens the moment a pairing is saved and narrows again if one is withdrawn.
 *   Nothing derived is persisted — same rule as everywhere else in this app.
 */

import draftData from '../data/draft-history.json';
import managersData from '../data/managers.json';
import rosterData from '../data/gm-roster.json';
import recordedPairings from '../data/manager-pairings.json';
import teamNameData from '../data/team-name-history.json';

/* -- shapes -------------------------------------------------------------------------------- */

export interface SeasonTeam {
  teamId: number;
  team: string;
  gm: string | null;
  gmResolved: boolean;
}

interface TeamSeasonRow { season: number; teams: SeasonTeam[] }

const RAW = (teamNameData as unknown as { seasons: TeamSeasonRow[] }).seasons;

interface DraftSeasonRow { season: number; order?: string[] }
const DRAFTS = (draftData as unknown as { seasons: DraftSeasonRow[] }).seasons;

/* -- who has ever managed a team -------------------------------------------------------------- */

export interface Manager {
  id: string;
  name: string;
  status: 'current' | 'former';
  /** Overrides the two-letter badge where initials collide — JOHN and JOHNW both give "JO". */
  mark?: string;
  /**
   * Fixes the badge colour. Assigned once and never recomputed, because deriving the hue from a
   * manager's position in a sorted list means adding an eleventh manager silently re-tints the
   * other ten — every board in the app changes colour overnight for no reason anybody can see.
   */
  order?: number;
  /**
   * What the app lets him do. The roster is the only place this is decided.
   *
   * `viewer` is the one that holds no team — somebody who signs in to watch. He has a roster
   * entry because he has a name and a badge and the app has to render him; he has no `team`, and
   * `currentManagers()` leaves him out, so he never appears in a seat, a draft order or a ledger.
   */
  role?: 'viewer' | 'manager' | 'commissioner' | 'admin';
  /** The 2026 seat, for a current manager. */
  team?: string;
  seasons?: number[];
  teamNames?: string[];
  coManagerOf?: string;
  note?: string;
  /** Added in the app rather than shipped with the build. */
  addedBy?: string;
  addedAt?: string;
}

export interface FranchiseLink { gm: string; from: number; to: number | null; with?: string }
export interface Franchise { id: string; label: string; chain: FranchiseLink[]; evidence: string }

const ROSTER = rosterData as unknown as {
  source: string; note: string; managers: Manager[]; franchises: Franchise[];
};

const BUNDLED: Manager[] = ROSTER.managers;
export const ROSTER_SOURCE = ROSTER.source;

/**
 * The roster, overlaid.
 *
 * The ten current managers and the five who have left ship with the build. Everything added
 * afterwards — a manager joining, or one of the names still missing from 2003-2008 that somebody
 * finally remembers — lands on top of that, exactly like the pairings do. Same reasoning: what is
 * known now belongs in the build, and what a commissioner adds must not require a redeploy.
 *
 * Nothing here is ever deleted. A manager who leaves becomes `former`, because his seasons are
 * still in the record and a career table cannot cite a man the roster has forgotten.
 */
let ADDED: Manager[] = [];
let ROSTER_VERSION = 0;
const rosterWatchers = new Set<() => void>();

function merged(): Manager[] {
  const by = new Map(BUNDLED.map((m) => [m.id, m]));
  for (const m of ADDED) by.set(m.id, { ...by.get(m.id), ...m });
  return [...by.values()];
}

let cache: { v: number; list: Manager[] } | null = null;
/** Every manager the league has ever had, current and former. */
export function allManagers(): Manager[] {
  if (!cache || cache.v !== ROSTER_VERSION) cache = { v: ROSTER_VERSION, list: merged() };
  return cache.list;
}

/** Kept as a live getter so existing call sites read through the overlay. */
export const MANAGERS: Manager[] = new Proxy([] as Manager[], {
  get: (_t, prop) => Reflect.get(allManagers(), prop),
  has: (_t, prop) => Reflect.has(allManagers(), prop),
  ownKeys: () => Reflect.ownKeys(allManagers()),
  getOwnPropertyDescriptor: (_t, prop) =>
    Reflect.getOwnPropertyDescriptor(allManagers(), prop),
}) as Manager[];

export const FRANCHISES: Franchise[] = ROSTER.franchises;

export const rosterVersion = (): number => ROSTER_VERSION;

export function setAddedManagers(list: readonly Manager[]): void {
  ADDED = [...list];
  ROSTER_VERSION += 1;
  for (const w of rosterWatchers) w();
}

export function onRosterChanged(fn: () => void): () => void {
  rosterWatchers.add(fn);
  return () => {
    rosterWatchers.delete(fn);
  };
}

const byId = () => new Map(allManagers().map((m) => [m.id, m]));

/** "John Wardach" rather than "JOHNW" — the league has had two Johns. */
export const managerName = (id: string): string => byId().get(id.toUpperCase())?.name ?? id;
export const isFormer = (id: string): boolean => byId().get(id.toUpperCase())?.status === 'former';
export const managerMark = (id: string): string | undefined => byId().get(id.toUpperCase())?.mark;

/**
 * The letters that actually go in the badge. ALWAYS TWO (Tim, 22 August).
 *
 * It used to escalate — three letters, then four — while a prefix collided with somebody else's,
 * which is why RAJAN read "RAJ" and VISHAL read "VIS" in a 20px circle built for two. A third
 * letter in that space is smaller type, tighter tracking and a mark that no longer matches the
 * others on the row, to solve a collision between two managers who are almost never on the same
 * screen.
 *
 * So: first two letters, and where those are taken, the first letter plus the first LATER letter
 * that is free — RAJAN keeps "RA" and RANDY becomes "RN", VISHAL keeps "VI" and VIVAN "VV". The
 * current ten need none of this (AM, AR, JO, LU, MA, RA, RY, SA, TI, VI are all distinct); it is
 * for the former managers who share a records board with them.
 *
 * PRIORITY IS ROSTER ORDER, so the man who is in the league keeps the obvious mark and the one who
 * left takes the derived one. Some collisions letters cannot solve at all — JOHN and JOHNW are
 * "JO" whichever way you cut them — and those carry an explicit two-letter mark in the roster
 * instead (John Wardach is JW).
 */
export function badgeMark(id: string): string {
  const fixed = managerMark(id);
  if (fixed) return fixed.slice(0, 2).toUpperCase();
  const me = id.toUpperCase();
  const first = me.slice(0, 2);
  // Everybody ahead of him in the roster who has no explicit mark of his own.
  const ahead = allManagers()
    .filter((m) => !m.mark)
    .map((m) => m.id.toUpperCase());
  const taken = new Set(ahead.slice(0, Math.max(0, ahead.indexOf(me))).map((x) => x.slice(0, 2)));
  if (!taken.has(first)) return first;
  for (const ch of me.slice(2)) {
    const alt = `${me[0]}${ch}`;
    if (!taken.has(alt)) return alt;
  }
  // Two letters that clash beats three that do not — the mark has to fit the circle.
  return first;
}
/**
 * The shortest name that is still unambiguous.
 *
 * "Tim Brown" is right on a roster and wrong on a draft board column; "Tim" is right on both. But
 * "John" is only right while there is one John, and this league has had two — so a first name is
 * used unless somebody else shares it, in which case the full name comes back.
 */
export function managerShort(id: string): string {
  const me = byId().get(id.toUpperCase());
  if (!me) return id;
  const first = me.name.split(/\s+/)[0]!;
  const shared = allManagers().some(
    (m) => m.id !== me.id && !m.coManagerOf && m.name.split(/\s+/)[0] === first,
  );
  return shared ? me.name : first;
}

export const managerRole = (id: string): 'viewer' | 'manager' | 'commissioner' | 'admin' =>
  byId().get(id.toUpperCase())?.role ?? 'manager';
export const isAdded = (id: string): boolean => ADDED.some((m) => m.id === id.toUpperCase());

/**
 * Whether the roster has ever heard of this id.
 *
 * The archive's older seasons record a SEAT LABEL where a manager should be — `ROID RAGE`,
 * `TEXAS HOLD 'EM` — because the workbook printed the team name and nobody has since said whose
 * it was. Anything reading such a label off a pick needs to know it is holding a label rather
 * than a manager, instead of finding out by getting a fallback back.
 */
export const isManager = (id: string | null | undefined): boolean =>
  Boolean(id && byId().has(id.toUpperCase()));

/** Colour index. Explicit where the roster gives one, appended in order where it does not. */
export function hueOrder(id: string): number {
  const m = byId().get(id.toUpperCase());
  if (m?.order !== undefined) return m.order;
  const fallback = allManagers().findIndex((x) => x.id === id.toUpperCase());
  return fallback < 0 ? allManagers().length : fallback;
}

/**
 * The managers holding a team this season, in the order the app has always listed them.
 *
 * A SEAT IS REQUIRED, and that clause is doing real work rather than restating the filter above.
 * `validateManager` has always refused to save a current manager without a team name — "so the
 * board knows which seat is his" — but the access queue writes a manager record directly when a
 * request is approved, and where Yahoo reports no team it writes one with `status: 'current'` and
 * no `team` at all. That record then appeared in `GMS`, which is the list the draft order, the
 * ledgers, the "acting as" select and the committee's voting roll are all built from. The visible
 * symptom was a roll of eleven names against a ten-team league and a tally reading "11
 * represented" — 2015-I-1's quorum of 8 and super-majority of 7 are written against ten seats, so
 * an eleventh body on the roll quietly moves every threshold it is measured against.
 *
 * So the filter enforces here what the form enforces there: no seat, no seat-holder. A person in
 * that state still exists, still signs in and still has a badge — he is just not one of the ten,
 * and `seatless()` below is how a screen finds him and says so rather than letting him vanish.
 */
export function currentManagers(): Manager[] {
  return allManagers()
    .filter((m) => m.status === 'current' && !m.coManagerOf && Boolean(m.team?.trim()))
    .sort((a, b) => a.id.localeCompare(b.id));
}

/**
 * Current-status people with no seat — the ones `currentManagers()` now leaves out.
 *
 * Exported so the roster screen can show them under a heading rather than dropping them silently.
 * A manager who is missing only because somebody forgot to type his team name looks exactly like
 * a manager who was never added, and the two need different fixes.
 */
export function seatless(): Manager[] {
  return allManagers()
    .filter((m) => m.status === 'current' && !m.coManagerOf && !m.team?.trim())
    .sort((a, b) => a.id.localeCompare(b.id));
}

/**
 * Which manager owns a team name this season.
 *
 * The keeper rows, the farm log and the draft board are all keyed on TEAM name, because that is
 * what Yahoo exports. That mapping used to be a frozen file, which was fine until a seat could
 * change hands inside the app: seat Priya in Samant's chair and the keeper board would happily
 * go on attributing Samanticiders' roster to Samant.
 *
 * So the file is the floor and the roster wins on top of it.
 */
export function teamToGm(): Record<string, string> {
  const out: Record<string, string> = Object.fromEntries(
    Object.entries(managersData as Record<string, string>)
      .map(([team, gm]) => [team, gm.toUpperCase()]),
  );
  for (const m of allManagers()) {
    if (m.status !== 'current' || !m.team) continue;
    // A manager who took a seat also takes the team name attached to it, so drop the old holder.
    for (const [team, gm] of Object.entries(out)) if (gm === m.id) delete out[team];
    out[m.team] = m.id;
  }
  return out;
}

/** The manager behind a team name, falling back to the name shouted. */
export const gmOfTeam = (team: string): string =>
  teamToGm()[team] ?? team.toUpperCase();

/**
 * The team a manager holds this season. The inverse of `gmOfTeam`, and empty if he holds none.
 *
 * Exists because the obvious way to write it in a screen — scan the keys of the team→GM map —
 * was being done against a Proxy whose only trap is `get`. `Object.keys` on such a Proxy returns
 * the keys of its empty target, so the scan found nothing, every time, for everybody. The keeper
 * declaration screen came up with an empty roster and no error, which is the worst shape a bug
 * can take: it looks like a manager with nothing to keep.
 */
export function teamOfGm(gm: string): string {
  const want = gm.trim().toUpperCase();
  if (!want) return '';
  const map = teamToGm();
  return Object.keys(map).find((team) => map[team] === want) ?? '';
}

/* -- adding one -------------------------------------------------------------------------------- */

/**
 * Derive an id from a name, avoiding the ones already taken.
 *
 * Ids are permanent in a way names are not: pairings, trades and declarations are all keyed on
 * them, so a rename is cosmetic and a re-id is a migration. The rule is the league's own habit —
 * the first name, shouted — and where two managers share one, as the two Johns do, the surname
 * is walked one letter at a time until it is unique.
 */
export function suggestId(name: string, taken: readonly string[] = allManagers().map((m) => m.id)):
string {
  const parts = name.trim().toUpperCase().replace(/[^A-Z\s]/g, '').split(/\s+/).filter(Boolean);
  const first = parts[0] ?? '';
  const rest = parts.slice(1).join('');
  if (!first) return '';
  const used = new Set(taken.map((t) => t.toUpperCase()));
  if (!used.has(first)) return first;
  for (let i = 1; i <= rest.length; i += 1) {
    const candidate = first + rest.slice(0, i);
    if (!used.has(candidate)) return candidate;
  }
  for (let n = 2; n < 20; n += 1) {
    const candidate = `${first}${n}`;
    if (!used.has(candidate)) return candidate;
  }
  return first;
}

/** A badge mark that does not collide with anybody already on the roster. */
export function suggestMark(id: string, name: string): string | undefined {
  const two = (s: string) => s.slice(0, 2).toUpperCase();
  const others = allManagers().filter((m) => m.id !== id.toUpperCase());
  const mine = two(id);
  if (!others.some((m) => (m.mark ?? two(m.id)) === mine)) return undefined;

  const parts = name.trim().toUpperCase().split(/\s+/).filter(Boolean);
  const initials = parts.length > 1 ? `${parts[0]![0]}${parts[1]![0]}` : '';
  if (initials && !others.some((m) => (m.mark ?? two(m.id)) === initials)) return initials;
  for (let n = 3; n <= id.length; n += 1) {
    const candidate = id.slice(0, n).toUpperCase();
    if (!others.some((m) => (m.mark ?? two(m.id)) === candidate)) return candidate;
  }
  return id.slice(0, 3).toUpperCase();
}

/** Everything wrong with a proposed manager, in the order a person would fix it. */
export function validateManager(m: Partial<Manager>, editingId?: string): string[] {
  const out: string[] = [];
  const id = (m.id ?? '').toUpperCase();
  if (!m.name?.trim()) out.push('Give him a name.');
  if (!id) out.push('An id is required — it is how every record refers to him.');
  else if (!/^[A-Z][A-Z0-9]{1,11}$/.test(id)) {
    out.push('An id is 2-12 letters or digits, starting with a letter, and no spaces.');
  }
  if (id && id !== editingId?.toUpperCase() && allManagers().some((x) => x.id === id)) {
    out.push(`${id} is already taken — pick another id.`);
  }
  if (m.status === 'current' && !m.team?.trim()) {
    out.push('A current manager needs a team name, so the board knows which seat is his.');
  }
  return out;
}

/** `${season}|${NORMALISED TEAM NAME}` — see `pairKey`. */
export type PairKey = string;

/** The saved answers. Deliberately a plain record so it round-trips through any store. */
export type Pairings = Record<PairKey, string>;

/**
 * Team names are written three different ways across the archives — title case in the standings,
 * shouted in the draft results, with stray double spaces here and there. Normalise before
 * comparing anything.
 */
export const normTeam = (s: string): string =>
  s.normalize('NFKC').replace(/\s+/g, ' ').trim().toUpperCase();

export const pairKey = (season: number, team: string): PairKey => `${season}|${normTeam(team)}`;

/** Function declaration so the recorded-pairing table below can be built at module load. */
function pairKeyRaw(season: number, team: string): PairKey {
  return `${season}|${normTeam(team)}`;
}

/**
 * A raw manager field is usable as-is only when the scrape genuinely resolved one. Sniffing the
 * string is not sufficient — "'roid rage" reads like a person to a regex, and treating it as one
 * is what made the head-to-head grid asymmetric — so callers that have a `gmResolved` flag should
 * pass it rather than relying on this.
 */
export const named = (gm: string | null | undefined): gm is string =>
  Boolean(gm) && gm !== '--hidden--' && !/^team\s/i.test(gm!);

/* -- the overlay --------------------------------------------------------------------------- */

/**
 * Facts the archives cannot supply, shipped with the app.
 *
 * Who "'roid rage" was is not recoverable from any source — Yahoo hides the name and the
 * Draftboard sheet never wrote it down. It came out of Tim's memory, so it lives in the build
 * rather than in a store somebody has to re-populate. A commissioner's saved pairing still wins
 * over anything here, and withdrawing one falls back to this rather than to nothing.
 */
const RECORDED: Pairings = Object.fromEntries(
  (recordedPairings as unknown as { pairings: Array<{ season: number; team: string; gm: string }> })
    .pairings.map((p) => [pairKeyRaw(p.season, p.team), p.gm]),
);

export const RECORDED_SOURCE =
  (recordedPairings as unknown as { source: string }).source;

/** True when this answer came with the build rather than from a commissioner. */
export const isRecorded = (season: number, team: string): boolean =>
  pairKeyRaw(season, team) in RECORDED;

let PAIRINGS: Pairings = { ...RECORDED };
let VERSION = 0;
const watchers = new Set<() => void>();

/** Bumped on every change, so expensive derived tables can cache against it. */
export const pairingVersion = (): number => VERSION;

export const getPairings = (): Pairings => PAIRINGS;

export function setPairings(next: Pairings): void {
  // The recorded facts are the floor, not a default that a missing store row can erase.
  PAIRINGS = { ...RECORDED, ...next };
  VERSION += 1;
  for (const w of watchers) w();
}

/** React subscribes here so a saved pairing re-renders every board that depends on it. */
export function onPairingsChanged(fn: () => void): () => void {
  watchers.add(fn);
  return () => {
    watchers.delete(fn);
  };
}

/* -- the read path ------------------------------------------------------------------------- */

/**
 * The manager behind a row, or null if nobody knows yet.
 *
 * `resolved` is the archive's own flag where the file carries one. Pass it: `false` means the
 * scrape KNOWS the name in the gm field is a team, which is stronger information than any
 * heuristic applied to the string afterwards.
 */
export function gmFor(
  season: number,
  fields: { gm?: string | null; team?: string | null; gmResolved?: boolean },
): string | null {
  const { gm, team, gmResolved } = fields;
  if (gmResolved === true && named(gm)) return gm;
  if (gmResolved === undefined && named(gm)) return gm;
  // Unresolved: the gm field, if present at all, is holding a team name.
  for (const candidate of [team, gm]) {
    if (!candidate) continue;
    const hit = PAIRINGS[pairKey(season, candidate)];
    if (hit) return hit;
  }
  return null;
}

/* -- a team nobody has put a name to ---------------------------------------------------------- */

/**
 * AN UNPAIRED TEAM IS ITS OWN MANAGER (Tim, 8 September: "pretend the unpaired 2003-2006
 * identities are standalone identities carrying their own team name, likely never to be paired").
 *
 * The archive's first four seasons are Yahoo pages with team names and no people behind them, and
 * five of those teams belong to men this league has lost touch with. Treating them as a MISSING
 * pairing made every career total in the app wait on a repair that is never coming: `careerTable`
 * skipped any season with an unresolved row, which meant it skipped 2003 through 2006 entirely,
 * which meant the Managers page showed ten dashes under `Titles` and `Career W-L`.
 *
 * Treating them as PEOPLE fixes that and is also the truer reading. `THE GREEN LANTERN` finished
 * where he finished and beat who he beat; the league simply knows him by his team rather than his
 * name. So he gets an identity of his own, and the ten men who played against him get their four
 * missing seasons back.
 *
 * The `#` is the whole safety mechanism: a GM id is a bare uppercase word, so a standalone id can
 * never collide with one, and any screen that has not been taught about them shows a visibly odd
 * string rather than quietly merging a team into a person. Pairing one still works and still moves
 * that season across — this is a floor, not a lock.
 */
export const STANDALONE = '#';

/** `THE GREEN LANTERN` -> `#THE GREEN LANTERN`. */
export const standaloneId = (team: string): string => `${STANDALONE}${team.trim()}`;

export const isStandalone = (id: string): boolean => id.startsWith(STANDALONE);

/** The team name back out, for a screen that wants to print it. */
export const standaloneName = (id: string): string => (
  isStandalone(id) ? id.slice(STANDALONE.length) : id
);

/**
 * Who a standings row belongs to: the manager it is paired to, or the team itself.
 *
 * Never null, which is the point — a caller using this cannot be tempted to drop the row, and
 * dropping the row is what took four seasons off everybody's career.
 */
export function identityFor(
  season: number,
  fields: { gm?: string | null; team?: string | null; gmResolved?: boolean },
): string {
  const paired = gmFor(season, fields);
  if (paired) return paired;
  const team = fields.team ?? fields.gm ?? '';
  return team ? standaloneId(team) : standaloneId('unknown');
}

/* -- the board a commissioner works through ------------------------------------------------ */

export interface Suggestion {
  gm: string;
  /** 0-1. Anything at 1 is an exact name carryover and is safe to accept in bulk. */
  confidence: number;
  why: string;
}

export interface OpenTeam {
  season: number;
  teamId: number;
  team: string;
  key: PairKey;
  /** Set once somebody has paired it. */
  assigned: string | null;
  suggestions: Suggestion[];
}

export interface SeasonBoard {
  season: number;
  /** Teams still needing a manager, including ones only the overlay has answered. */
  open: OpenTeam[];
  /** Names Yahoo resolved by itself, for context while pairing the rest. */
  known: Array<{ teamId: number; team: string; gm: string }>;
  /** True when every team in the season has a manager from one source or the other. */
  complete: boolean;
}

/** Words that carry no identity. "The Insane Israeli" and "The Great White Hype" share nothing. */
const STOP = new Set([
  'THE', 'A', 'AN', 'AND', 'OF', 'MY', 'TEAM', 'FC', 'II', 'INC', 'LLC', 'TO', 'FOR', 'IN', 'ON',
  'IS', 'IT', 'AT', 'BY', 'WE', 'US', 'ME', 'YOU', 'ALL', 'NOT', 'NO', 'OR', 'UP', 'THEM', 'THIS',
  'THAT', 'ARE', 'BE', 'DA', 'DE', 'GO', 'NEW', 'BIG', 'OLD', 'LIL', 'BOYS', 'GUYS', 'SQUAD',
  'CLUB', 'BOWL', 'LEAGUE', 'FANTASY', 'FOOTBALL', 'YEAR', 'NEXT', 'FIRST', 'LAST',
]);

const tokens = (name: string): string[] =>
  normTeam(name)
    .replace(/[^A-Z0-9' ]+/g, ' ')
    .split(' ')
    .map((t) => t.replace(/^'+|'+$/g, ''))
    .filter((t) => t.length >= 3 && !STOP.has(t));

/** Every (gm, team name, season) the app is sure about — Yahoo's own plus the overlay's. */
function corpus(p: Pairings): Array<{ gm: string; team: string; season: number; fromOverlay: boolean }> {
  const out: Array<{ gm: string; team: string; season: number; fromOverlay: boolean }> = [];
  for (const s of RAW) {
    for (const t of s.teams) {
      if (t.gmResolved && named(t.gm)) {
        out.push({ gm: t.gm!, team: t.team, season: s.season, fromOverlay: false });
      } else {
        const hit = p[pairKey(s.season, t.team)];
        if (hit) out.push({ gm: hit, team: t.team, season: s.season, fromOverlay: true });
      }
    }
  }
  return out;
}

/** Distinctiveness: a word used by six different managers identifies nobody. */
function rarity(all: Array<{ gm: string; team: string }>): Map<string, number> {
  const owners = new Map<string, Set<string>>();
  for (const c of all) {
    for (const tok of tokens(c.team)) {
      const set = owners.get(tok) ?? new Set<string>();
      set.add(c.gm);
      owners.set(tok, set);
    }
  }
  const out = new Map<string, number>();
  for (const [tok, set] of owners) out.set(tok, 1 / set.size);
  return out;
}


/* -- what the draft archive gives away ------------------------------------------------------ */

/**
 * The Draftboard workbook's own draft orders are a second, independent witness — and a much
 * better one than the team names.
 *
 * Whoever kept the sheet wrote each season's order down inconsistently: mostly team names, but
 * for the managers he knew personally he just wrote the manager. 2003's order reads
 * ["RAJAN", "AMIT", "HOWBOUTTHEMHOKIES!!", "FOOTBALL TEAM", …]. That inconsistency is the gift.
 * The order lists every team in the league exactly once, so if eight of the ten entries match a
 * Yahoo team name outright, the two names left over MUST be the two managers he named — the only
 * open question is which way round.
 *
 * That is a constraint, not a guess, and it is worth far more than word-similarity: it names the
 * candidate SET with certainty and lets elimination do the rest. Pair ARI to "The Insane Israeli"
 * (he used the name again in 2015) and 2003's three-way choice becomes a two-way one.
 */
interface SeatConstraint {
  season: number;
  /** Managers the archive names for this season without saying which team. */
  gms: string[];
  /** The Yahoo team names it never mentions — one of these belongs to each manager above. */
  teams: string[];
}

/** Punctuation and spacing differ between the two sources: "Leavenworth Lefty #7" vs the sheet's
 * "LEAVENWORTH LEFTY#7", "'roid rage" vs "ROID RAGE". Compare on letters and digits only. */
const looseKey = (s: string) => normTeam(s).replace(/[^A-Z0-9]/g, '');

/** Every manager name Yahoo has ever confirmed — the vocabulary that tells a GM from a team. */
const GM_VOCAB = new Set<string>(
  RAW.flatMap((s) => s.teams.filter((t) => t.gmResolved && named(t.gm)).map((t) => t.gm!)),
);

const SEATS: SeatConstraint[] = (() => {
  const out: SeatConstraint[] = [];
  for (const d of DRAFTS) {
    const season = RAW.find((s) => s.season === d.season);
    if (!season || !d.order?.length) continue;

    const byLoose = new Map(season.teams.map((t) => [looseKey(t.team), t.team]));
    const matched = new Set<string>();
    const gms: string[] = [];
    let unusable = false;

    for (const entry of d.order) {
      const k = looseKey(entry);
      const team = byLoose.get(k);
      if (team) {
        // The same team named twice would break the one-seat-per-team assumption this rests on.
        if (matched.has(team)) unusable = true;
        matched.add(team);
        continue;
      }
      if (GM_VOCAB.has(normTeam(entry)) && !gms.includes(normTeam(entry))) {
        gms.push(normTeam(entry));
        continue;
      }
      // An entry that is neither a team this season nor a manager the league knows: the archive
      // and Yahoo disagree about who was in the league, so infer nothing at all from this season.
      unusable = true;
    }

    const leftover = season.teams.filter((t) => !matched.has(t.team));
    if (unusable || gms.length === 0 || gms.length !== leftover.length) continue;
    out.push({ season: d.season, gms, teams: leftover.map((t) => t.team) });
  }
  return out;
})();

/**
 * Resolve one season's constraint as far as pure elimination will take it.
 *
 * Two moves, applied until nothing changes — the same pair a person uses on a sudoku:
 *   a team with one candidate left is that manager;
 *   a manager who fits only one remaining team is that team's.
 *
 * `hint` is the name evidence, used only to NARROW a candidate set, never to widen one.
 */
function solveSeat(
  c: SeatConstraint,
  p: Pairings,
  hint: (team: string) => Map<string, number>,
): Map<string, { gm: string; why: string }> {
  const fixed = new Map<string, { gm: string; why: string }>();
  const cand = new Map<string, Set<string>>();
  const pool = new Set(c.gms);

  for (const team of c.teams) {
    const already = RAW.find((s) => s.season === c.season)!.teams
      .find((t) => t.team === team)!;
    const settled = already.gmResolved && named(already.gm)
      ? already.gm!
      : p[pairKey(c.season, team)];
    if (settled) {
      fixed.set(team, { gm: settled, why: 'already paired' });
      pool.delete(settled);
    } else {
      cand.set(team, new Set(c.gms));
    }
  }
  for (const set of cand.values()) for (const gm of [...set]) if (!pool.has(gm)) set.delete(gm);

  // Name evidence narrows, so an exact carryover inside a constrained season settles it outright:
  // ARI used "The Insane Israeli" again in 2015, and the archive already proved he is one of the
  // three managers 2003 leaves unnamed. Two independent witnesses, same answer.
  const byName = new Map<string, string>();
  for (const [team, set] of cand) {
    const h = hint(team);
    const strong = [...set].filter((g) => (h.get(g) ?? 0) >= 0.99);
    if (strong.length === 1) {
      set.clear();
      set.add(strong[0]!);
      byName.set(team, strong[0]!);
    }
  }
  for (let pass = 0; pass < 12; pass += 1) {
    let moved = false;

    for (const [team, set] of cand) {
      if (set.size !== 1) continue;
      const gm = [...set][0]!;
      fixed.set(team, {
        gm,
        why: byName.get(team) === gm
          ? `he used this exact name again later, and the ${c.season} draft archive independently `
            + `names him as one of its unlisted managers (${c.gms.join(', ')})`
          : `the ${c.season} draft archive names ${c.gms.join(', ')} without saying which team, `
            + `and ${gm} is the only one left`,
      });
      cand.delete(team);
      for (const other of cand.values()) other.delete(gm);
      moved = true;
    }

    for (const gm of pool) {
      if ([...fixed.values()].some((f) => f.gm === gm)) continue;
      const homes = [...cand.entries()].filter(([, set]) => set.has(gm));
      if (homes.length !== 1) continue;
      const [team] = homes[0]!;
      fixed.set(team, {
        gm,
        why: `${gm} is named in the ${c.season} draft archive and "${team}" is the only team he `
          + 'can be',
      });
      cand.delete(team);
      moved = true;
    }

    if (!moved) break;
  }

  // Anything still open keeps its narrowed candidate set, which is worth showing.
  for (const [team, set] of cand) {
    if (set.size === 0 || set.size === 1) continue;
    fixed.set(team, { gm: '', why: [...set].join('|') });
  }
  return fixed;
}

function suggestFor(
  season: number,
  team: string,
  known: Array<{ gm: string; team: string; season: number }>,
  idf: Map<string, number>,
  takenThisSeason: Set<string>,
): Suggestion[] {
  const scores = new Map<string, { score: number; why: string }>();
  const bump = (gm: string, score: number, why: string) => {
    const prev = scores.get(gm);
    if (!prev || score > prev.score) scores.set(gm, { score, why });
  };

  const mine = new Set(tokens(team));
  for (const c of known) {
    if (c.season === season) continue;
    const gap = Math.abs(c.season - season);
    // A name carried from one year to the next is the same joke; the same name eight years
    // apart is more likely a coincidence, so nearby seasons count for more.
    const near = 1 / (1 + gap * 0.25);

    if (normTeam(c.team) === normTeam(team)) {
      bump(c.gm, 1, `used this exact name in ${c.season}`);
      continue;
    }
    const shared = tokens(c.team).filter((t) => mine.has(t));
    if (!shared.length) continue;
    const weight = shared.reduce((n, t) => n + (idf.get(t) ?? 0), 0);
    const score = Math.min(0.95, weight * near);
    if (score > 0.12) {
      bump(c.gm, score, `"${shared.join('", "')}" — also in "${c.team}" (${c.season})`);
    }
  }

  return [...scores.entries()]
    .map(([gm, s]) => ({
      gm,
      // A GM already holding a team this season cannot hold a second one. Keep him visible but
      // sink him: the right answer is sometimes to fix the other pairing first.
      confidence: takenThisSeason.has(gm) ? s.score * 0.2 : s.score,
      why: takenThisSeason.has(gm) ? `${s.why} — but already paired this season` : s.why,
    }))
    .sort((a, b) => b.confidence - a.confidence)
    .slice(0, 5);
}


/**
 * Who was around, but is missing from this season.
 *
 * Only useful when a season is nearly solved: 2009 and 2010 name nine of ten managers and leave
 * "'roid rage" hanging, and MATT is the one man who plays either side of that gap without
 * appearing in it. That is not proof — a team can change hands and keep its name — but it is
 * exactly the prompt that makes somebody remember, which is what this screen is for.
 *
 * Deliberately silent on 2003-2008, where nothing is known and every manager would qualify.
 */
function neighbourHint(
  season: number,
  accountedFor: Set<string>,
  stillOpen: number,
  p: Pairings,
): Suggestion[] {
  if (stillOpen > 3) return [];
  const near = new Set<string>();
  for (const s of RAW) {
    if (s.season === season || Math.abs(s.season - season) > 2) continue;
    for (const t of s.teams) {
      if (t.gmResolved && named(t.gm)) near.add(t.gm!);
      else {
        const hit = p[pairKey(s.season, t.team)];
        if (hit) near.add(hit);
      }
    }
  }
  const missing = [...near].filter((g) => !accountedFor.has(g));
  if (missing.length === 0 || missing.length > 3) return [];
  return missing.map((gm) => ({
    gm,
    confidence: 0.3,
    why: `played either side of ${season} but is not on any named team in it`,
  }));
}

/**
 * Combine the two witnesses.
 *
 * The archive's constraint outranks word-similarity, because it is a fact about who was in the
 * league rather than a guess about a joke: where it settles a team, that answer is certain and
 * everything else is dropped. Where it only narrows the field, the narrowing is applied to the
 * name suggestions — a manager the archive rules out is not a candidate however his old team
 * names read.
 */
function merge(
  byName: Suggestion[],
  seat: { gm: string; why: string } | undefined,
  takenThisSeason: Set<string>,
): Suggestion[] {
  const best = new Map<string, Suggestion>();
  for (const sg of byName) {
    const prev = best.get(sg.gm);
    if (!prev || sg.confidence > prev.confidence) best.set(sg.gm, sg);
  }
  const ranked = [...best.values()].sort((a, b) => b.confidence - a.confidence).slice(0, 5);
  if (!seat) return ranked;

  if (seat.gm) {
    const clash = takenThisSeason.has(seat.gm);
    return [{
      gm: seat.gm,
      confidence: clash ? 0.2 : 1,
      why: clash ? `${seat.why} — but already paired this season` : seat.why,
    }];
  }

  // Narrowed, not settled: seat.why carries the surviving candidates.
  const allowed = new Set(seat.why.split('|').filter(Boolean));
  const kept = ranked.filter((x) => allowed.has(x.gm));
  const scored = [...allowed].map((gm) => {
    const hit = kept.find((k) => k.gm === gm);
    const base = 1 / allowed.size;
    return {
      gm,
      confidence: Math.min(0.9, hit ? Math.max(hit.confidence, base + 0.25) : base),
      why: hit
        ? `${hit.why} — and the draft archive narrows it to ${[...allowed].join(' or ')}`
        : `the draft archive narrows it to ${[...allowed].join(' or ')}`,
    };
  });
  return scored.sort((a, b) => b.confidence - a.confidence);
}

/**
 * The whole pairing board, newest season first, seasons with nothing to do dropped.
 *
 * Takes the pairing map explicitly so the cascade below can ask "what would the board look like
 * if I accepted these?" without touching what the app is currently showing.
 */
export function pairingBoard(p: Pairings = PAIRINGS): SeasonBoard[] {
  const known = corpus(p);
  const idf = rarity(known);
  const out: SeasonBoard[] = [];

  for (const s of RAW) {
    const seat = SEATS.find((c) => c.season === s.season);
    const nameEvidence = (team: string) => new Map(
      suggestFor(s.season, team, known, idf, new Set()).map((x) => [x.gm, x.confidence]),
    );
    const solved = seat ? solveSeat(seat, p, nameEvidence) : new Map();
    const open: OpenTeam[] = [];
    const settled: Array<{ teamId: number; team: string; gm: string }> = [];
    const taken = new Set<string>();

    for (const t of s.teams) {
      if (t.gmResolved && named(t.gm)) {
        settled.push({ teamId: t.teamId, team: t.team, gm: t.gm! });
        taken.add(t.gm!);
      } else {
        const assigned = p[pairKey(s.season, t.team)] ?? null;
        if (assigned) taken.add(assigned);
      }
    }

    const stillOpen = s.teams.filter(
      (t) => !(t.gmResolved && named(t.gm)) && !p[pairKey(s.season, t.team)],
    ).length;

    for (const t of s.teams) {
      if (t.gmResolved && named(t.gm)) continue;
      const assigned = p[pairKey(s.season, t.team)] ?? null;
      const others = new Set(taken);
      if (assigned) others.delete(assigned);
      open.push({
        season: s.season,
        teamId: t.teamId,
        team: t.team,
        key: pairKey(s.season, t.team),
        assigned,
        suggestions: merge(
          [
            ...suggestFor(s.season, t.team, known, idf, others),
            ...neighbourHint(s.season, taken, stillOpen, p),
          ],
          solved.get(t.team),
          others,
        ),
      });
    }

    if (open.length === 0) continue;
    out.push({
      season: s.season,
      open,
      known: settled,
      complete: open.every((o) => o.assigned),
    });
  }

  return out;
}

/** How much of the job is done — the number the settings page leads with. */
export function pairingProgress(): { open: number; done: number; seasons: number[] } {
  const board = pairingBoard();
  const all = board.flatMap((s) => s.open);
  return {
    open: all.filter((o) => !o.assigned).length,
    done: all.filter((o) => o.assigned).length,
    seasons: board.filter((s) => !s.complete).map((s) => s.season),
  };
}

export interface ConfidentPairing {
  key: PairKey; season: number; team: string; gm: string; why: string;
  /** Which round of the cascade found it. Round 1 is a match against Yahoo's own names. */
  pass: number;
}

/** One round: exact name carryover only, and never two teams in a season onto one manager. */
function confidentPass(p: Pairings): ConfidentPairing[] {
  const out: ConfidentPairing[] = [];
  for (const s of pairingBoard(p)) {
    for (const o of s.open) {
      if (o.assigned) continue;
      const best = o.suggestions[0];
      if (!best || best.confidence < 1) continue;
      // Two unresolved teams in one season both matching the same GM is exactly the case a
      // bulk accept must not guess at.
      if (out.some((x) => x.season === o.season && x.gm === best.gm)) continue;
      out.push({ key: o.key, season: o.season, team: o.team, gm: best.gm, why: best.why, pass: 0 });
    }
  }
  return out;
}

/**
 * Every pairing the engine is certain of, cascaded.
 *
 * One pass only matches against names Yahoo resolved, which stops at 2009. But pairing 2008's
 * "Samanticiders" to SAMANT makes the identical 2007 name certain too, and that makes 2006
 * certain, and so on back to 2003 — the joke is the thread. So this applies each round to a
 * working copy and runs again until nothing new appears, without touching the live overlay.
 *
 * Offered as one button, because working through the obvious ones by hand is how a commissioner
 * gives up before reaching the ones that need thinking about.
 */
export function confidentPairings(): ConfidentPairing[] {
  const work: Pairings = { ...PAIRINGS };
  const out: ConfidentPairing[] = [];
  for (let pass = 1; pass <= 12; pass += 1) {
    const found = confidentPass(work).filter((f) => !(f.key in work));
    if (found.length === 0) break;
    for (const f of found) {
      work[f.key] = f.gm;
      out.push({ ...f, pass });
    }
  }
  return out;
}

/** Two teams in one season pointing at the same manager. Always a mistake; never silent. */
export function pairingConflicts(): Array<{ season: number; gm: string; teams: string[] }> {
  const out: Array<{ season: number; gm: string; teams: string[] }> = [];
  for (const s of RAW) {
    const byGm = new Map<string, string[]>();
    for (const t of s.teams) {
      const gm = t.gmResolved && named(t.gm) ? t.gm! : PAIRINGS[pairKey(s.season, t.team)];
      if (!gm) continue;
      byGm.set(gm, [...(byGm.get(gm) ?? []), t.team]);
    }
    for (const [gm, teams] of byGm) {
      if (teams.length > 1) out.push({ season: s.season, gm, teams });
    }
  }
  return out;
}

/**
 * Managers to offer in the picker.
 *
 * Everybody the league has ever had, not just the current ten — most of the open seasons belong
 * to men who left, and a picker that cannot name John Wardach cannot solve 2009.
 */
export function knownGms(): string[] {
  const set = new Set<string>(MANAGERS.filter((m) => !m.coManagerOf).map((m) => m.id));
  for (const s of RAW) for (const t of s.teams) if (t.gmResolved && named(t.gm)) set.add(t.gm!);
  for (const gm of Object.values(PAIRINGS)) set.add(gm);
  return [...set].sort();
}

/** Every season the archives cover, newest first. */
export const ALL_SEASONS = RAW.map((s) => s.season);
